import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Check,
  X,
  Globe,
  Palette,
  Database,
  Users,
  RefreshCw,
  ExternalLink,
  Copy,
  Play,
  Clock,
  AlertCircle,
  ShoppingCart,
  Upload,
  Coins
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function BrandManagement() {
  const { isSuperAdmin } = useAuth();
  const { refreshBrands } = useChat();
  
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [globalDataBridgeUrl, setGlobalDataBridgeUrl] = useState('');
  const [syncStatuses, setSyncStatuses] = useState({});
  const [syncingBrands, setSyncingBrands] = useState({});
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    logo_url: '',
    integration_type: 'csv_upload',
    data_bridge_api_key: '',
    karix_sender_id: '',
    theme_primary_color: '#8B5CF6',
    theme_secondary_color: '#EC4899',
    point_value: 1.0,  // Default: 1 point = ₹1
    is_active: true,
    admin_email: '',
    admin_password: '',
    admin_name: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch sync status for all brands
  const fetchSyncStatuses = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/sync/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const statusMap = {};
        (data.brands || []).forEach(s => {
          statusMap[s.brand_id] = s;
        });
        setSyncStatuses(statusMap);
      }
    } catch (error) {
      console.error('Error fetching sync statuses:', error);
    }
  }, []);

  // Trigger sync for a specific brand
  const triggerBrandSync = async (brand) => {
    if (!brand.data_bridge_api_key) {
      toast.error('Please configure API key before syncing');
      return;
    }
    
    setSyncingBrands(prev => ({ ...prev, [brand.brand_id]: true }));
    
    try {
      const response = await fetch(`${API}/api/sync/trigger/${brand.brand_id}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Sync started for ${brand.name}`);
        // Poll for status updates
        const pollInterval = setInterval(async () => {
          await fetchSyncStatuses();
          const status = syncStatuses[brand.brand_id];
          if (status?.status !== 'syncing') {
            clearInterval(pollInterval);
            setSyncingBrands(prev => ({ ...prev, [brand.brand_id]: false }));
            if (status?.status === 'completed') {
              toast.success(`Sync completed for ${brand.name}`);
            } else if (status?.status === 'failed') {
              toast.error(`Sync failed for ${brand.name}`);
            }
          }
        }, 3000);
        
        // Stop polling after 5 minutes max
        setTimeout(() => {
          clearInterval(pollInterval);
          setSyncingBrands(prev => ({ ...prev, [brand.brand_id]: false }));
        }, 300000);
      } else {
        toast.error(data.message || 'Failed to start sync');
        setSyncingBrands(prev => ({ ...prev, [brand.brand_id]: false }));
      }
    } catch (error) {
      console.error('Error triggering sync:', error);
      toast.error('Error starting sync');
      setSyncingBrands(prev => ({ ...prev, [brand.brand_id]: false }));
    }
  };

  // Format last sync time in IST
  const formatLastSync = (syncStatus) => {
    if (!syncStatus?.last_sync) return 'Never';
    const date = new Date(syncStatus.last_sync);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  // Get sync status badge
  const getSyncStatusBadge = (brand) => {
    const status = syncStatuses[brand.brand_id];
    const isSyncing = syncingBrands[brand.brand_id] || status?.status === 'syncing';
    
    if (!brand.data_bridge_api_key) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
          <AlertCircle className="w-3 h-3" /> No API Key
        </span>
      );
    }
    
    if (isSyncing) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
          <Loader2 className="w-3 h-3 animate-spin" /> Syncing...
        </span>
      );
    }
    
    if (!status || status.status === 'never_synced') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-orange-500/20 text-orange-400">
          <Clock className="w-3 h-3" /> Never Synced
        </span>
      );
    }
    
    if (status.status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
          <X className="w-3 h-3" /> Failed
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
        <Check className="w-3 h-3" /> {formatLastSync(status)}
      </span>
    );
  };

  // Fetch global Data Bridge URL from platform settings
  const fetchGlobalSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/settings/data-bridge-url`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGlobalDataBridgeUrl(data.data_bridge_url || '');
      }
    } catch (error) {
      console.error('Error fetching global settings:', error);
    }
  }, []);

  // Fetch all brands
  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/brands`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
      } else {
        toast.error('Failed to load brands');
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Error loading brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBrands();
      fetchGlobalSettings();
      fetchSyncStatuses();
      
      // Poll sync statuses every 30 seconds
      const interval = setInterval(fetchSyncStatuses, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchBrands, fetchGlobalSettings, fetchSyncStatuses, isSuperAdmin]);

  // Filter brands by search term
  const filteredBrands = brands.filter(b => 
    b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.subdomain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Create brand
  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.subdomain) {
      toast.error('Please fill brand name and subdomain');
      return;
    }
    
    // Validate subdomain format
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!subdomainRegex.test(formData.subdomain.toLowerCase())) {
      toast.error('Subdomain can only contain lowercase letters, numbers, and hyphens');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/api/brands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          subdomain: formData.subdomain.toLowerCase()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const brandId = data.brand?.brand_id || data.brand_id;
        toast.success(`Brand "${formData.name}" created successfully`);
        
        // Create admin user if credentials provided
        if (formData.admin_email && formData.admin_password && brandId) {
          try {
            const userResponse = await fetch(`${API}/api/brands/${brandId}/users`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                email: formData.admin_email,
                password: formData.admin_password,
                name: formData.admin_name || formData.name + ' Admin',
                role: 'brand_admin'
              })
            });
            
            if (userResponse.ok) {
              toast.success(`Admin user created: ${formData.admin_email}`);
            } else {
              const userError = await userResponse.json();
              toast.error(`Brand created but admin user failed: ${userError.detail || 'Unknown error'}`);
            }
          } catch (userError) {
            console.error('Error creating admin user:', userError);
            toast.error('Brand created but failed to create admin user');
          }
        }
        
        setShowCreateModal(false);
        resetForm();
        fetchBrands();
        refreshBrands?.();
      } else {
        toast.error(data.detail || 'Failed to create brand');
      }
    } catch (error) {
      console.error('Error creating brand:', error);
      toast.error('Error creating brand');
    } finally {
      setSubmitting(false);
    }
  };

  // Update brand
  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    if (!selectedBrand) return;
    
    setSubmitting(true);
    try {
      const updateData = {
        name: formData.name,
        logo_url: formData.logo_url || null,
        theme_primary_color: formData.theme_primary_color,
        theme_secondary_color: formData.theme_secondary_color,
        point_value: formData.point_value || 1.0,
        is_active: formData.is_active
      };
      
      // Only include API key if it was changed
      if (formData.data_bridge_api_key) {
        updateData.data_bridge_api_key = formData.data_bridge_api_key;
      }
      
      const response = await fetch(`${API}/api/brands/${selectedBrand.brand_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Brand updated successfully');
        setShowEditModal(false);
        setSelectedBrand(null);
        resetForm();
        fetchBrands();
        refreshBrands?.();
      } else {
        toast.error(data.detail || 'Failed to update brand');
      }
    } catch (error) {
      console.error('Error updating brand:', error);
      toast.error('Error updating brand');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete brand
  const handleDeleteBrand = async () => {
    if (!selectedBrand) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/api/brands/${selectedBrand.brand_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Brand deleted successfully');
        setShowDeleteConfirm(false);
        setSelectedBrand(null);
        fetchBrands();
        refreshBrands?.();
      } else {
        toast.error(data.detail || 'Failed to delete brand');
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Error deleting brand');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subdomain: '',
      logo_url: '',
      integration_type: 'csv_upload',
      data_bridge_api_key: '',
      karix_sender_id: '',
      theme_primary_color: '#8B5CF6',
      theme_secondary_color: '#EC4899',
      point_value: 1.0,
      is_active: true,
      admin_email: '',
      admin_password: '',
      admin_name: ''
    });
  };

  const openEditModal = (brand) => {
    setSelectedBrand(brand);
    setFormData({
      name: brand.name || '',
      subdomain: brand.subdomain || '',
      logo_url: brand.logo_url || '',
      integration_type: brand.integration_type || 'csv_upload',
      data_bridge_api_key: '',  // Don't show existing API key for security
      karix_sender_id: brand.karix_sender_id || '',
      theme_primary_color: brand.theme?.primary_color || '#8B5CF6',
      theme_secondary_color: brand.theme?.secondary_color || '#EC4899',
      point_value: brand.point_value || 1.0,
      is_active: brand.is_active !== false
    });
    setShowEditModal(true);
  };

  const copySubdomain = (subdomain) => {
    const url = `${subdomain}AI.fundledata.com`;
    navigator.clipboard.writeText(url);
    toast.success(`Copied: ${url}`);
  };

  // Count users for a brand
  const getUserCount = (brandId) => {
    // This would need a separate API call in production
    return '—';
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Building2 className="w-12 h-12 mb-4 opacity-50" />
        <p>Only super admins can access Brand Management</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brand-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            Brand Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage brands across the Fundle Brain platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchBrands} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="add-brand-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Brand
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search brands..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="brand-search"
        />
      </div>

      {/* Brands Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredBrands.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Building2 className="w-12 h-12 mb-4 opacity-50" />
          <p>{searchTerm ? 'No brands match your search' : 'No brands created yet'}</p>
          {!searchTerm && (
            <Button className="mt-4" onClick={() => { resetForm(); setShowCreateModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Brand
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBrands.map((brand) => (
            <div
              key={brand.brand_id}
              className="rounded-xl border border-border bg-card p-5 hover:shadow-lg transition-all duration-200"
              data-testid={`brand-card-${brand.brand_id}`}
            >
              {/* Brand Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {brand.logo_url ? (
                    <img 
                      src={brand.logo_url} 
                      alt={brand.name}
                      className="w-12 h-12 object-contain rounded-lg bg-muted p-1"
                    />
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-bold"
                      style={{ backgroundColor: brand.theme?.primary_color || '#8B5CF6' }}
                    >
                      {brand.name?.[0]?.toUpperCase() || 'B'}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{brand.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Globe className="w-3 h-3" />
                      <span>{brand.subdomain || brand.brand_id}AI.fundledata.com</span>
                      <button 
                        onClick={() => copySubdomain(brand.subdomain || brand.brand_id)}
                        className="p-1 hover:bg-muted rounded"
                        title="Copy URL"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {brand.is_active !== false ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                      <Check className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                      <X className="w-3 h-3" /> Inactive
                    </span>
                  )}
                  {getSyncStatusBadge(brand)}
                </div>
              </div>

              {/* Brand Info */}
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Database className="w-3 h-3" /> API Key
                  </span>
                  <span className={brand.data_bridge_api_key ? 'text-green-400' : 'text-muted-foreground'}>
                    {brand.data_bridge_api_key ? 'Configured' : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Coins className="w-3 h-3" /> Point Value
                  </span>
                  <span className="text-amber-400 font-medium">
                    ₹{(brand.point_value || 1.0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Palette className="w-3 h-3" /> Theme
                  </span>
                  <div className="flex items-center gap-1">
                    <div 
                      className="w-4 h-4 rounded-full border border-border"
                      style={{ backgroundColor: brand.theme?.primary_color || '#8B5CF6' }}
                      title="Primary"
                    />
                    <div 
                      className="w-4 h-4 rounded-full border border-border"
                      style={{ backgroundColor: brand.theme?.secondary_color || '#EC4899' }}
                      title="Secondary"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Users className="w-3 h-3" /> Users
                  </span>
                  <span>{getUserCount(brand.brand_id)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-3 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => triggerBrandSync(brand)}
                  disabled={!brand.data_bridge_api_key || syncingBrands[brand.brand_id]}
                  data-testid={`sync-brand-${brand.brand_id}`}
                >
                  {syncingBrands[brand.brand_id] ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3 mr-1" />
                  )}
                  Sync Now
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(brand)}
                  data-testid={`edit-brand-${brand.brand_id}`}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => { setSelectedBrand(brand); setShowDeleteConfirm(true); }}
                  data-testid={`delete-brand-${brand.brand_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Brand Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Create New Brand
            </DialogTitle>
            <DialogDescription>
              Add a new brand to the Fundle Brain platform
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateBrand} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Brand Name *</Label>
                <Input
                  id="create-name"
                  placeholder="NewU"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="create-brand-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-subdomain">Subdomain *</Label>
                <div className="flex items-center">
                  <Input
                    id="create-subdomain"
                    placeholder="newu"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    required
                    className="rounded-r-none"
                    data-testid="create-brand-subdomain"
                  />
                  <span className="px-3 py-2 bg-muted border border-l-0 border-border rounded-r-md text-sm text-muted-foreground">
                    AI.fundledata.com
                  </span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-logo">Logo URL</Label>
              <Input
                id="create-logo"
                placeholder="https://example.com/logo.png"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                data-testid="create-brand-logo"
              />
            </div>

            {/* Integration Type Selection */}
            <div className="space-y-2">
              <Label>Data Integration Type *</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'data_bridge', label: 'Data Bridge', desc: 'Connect via Fundle Data Bridge API', icon: Database },
                  { id: 'pos', label: 'POS Integration', desc: 'Direct POS system connection', icon: ShoppingCart },
                  { id: 'csv_upload', label: 'CSV Upload', desc: 'Manual CSV file uploads', icon: Upload }
                ].map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, integration_type: type.id })}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      formData.integration_type === type.id
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/50'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 mb-1 ${formData.integration_type === type.id ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-muted-foreground">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Karix Sender ID */}
            <div className="space-y-2">
              <Label htmlFor="create-karix">Karix Sender ID</Label>
              <Input
                id="create-karix"
                placeholder="e.g., DBNEWU, SPYKAR"
                value={formData.karix_sender_id}
                onChange={(e) => setFormData({ ...formData, karix_sender_id: e.target.value.toUpperCase() })}
                data-testid="create-brand-karix"
              />
              <p className="text-xs text-muted-foreground">
                Sender ID for SMS campaigns via Karix
              </p>
            </div>

            {/* Point Value */}
            <div className="space-y-2">
              <Label htmlFor="create-point-value">Point Redemption Value (₹)</Label>
              <Input
                id="create-point-value"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="1.00"
                value={formData.point_value}
                onChange={(e) => setFormData({ ...formData, point_value: parseFloat(e.target.value) || 1.0 })}
                data-testid="create-brand-point-value"
              />
              <p className="text-xs text-muted-foreground">
                Value of 1 loyalty point in rupees (default: ₹1)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-primary">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="create-primary"
                    value={formData.theme_primary_color}
                    onChange={(e) => setFormData({ ...formData, theme_primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme_primary_color}
                    onChange={(e) => setFormData({ ...formData, theme_primary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-secondary">Secondary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="create-secondary"
                    value={formData.theme_secondary_color}
                    onChange={(e) => setFormData({ ...formData, theme_secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme_secondary_color}
                    onChange={(e) => setFormData({ ...formData, theme_secondary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data Bridge Connection
              </Label>
              <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">API URL (Global)</Label>
                  <div className="flex items-center px-3 py-2 bg-background rounded-md text-sm border border-border">
                    <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
                    <span className="text-muted-foreground truncate">{globalDataBridgeUrl || 'Not configured'}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="create-api-key">API Key *</Label>
                  <Input
                    id="create-api-key"
                    type="password"
                    placeholder="Enter brand's Data Bridge API key"
                    value={formData.data_bridge_api_key}
                    onChange={(e) => setFormData({ ...formData, data_bridge_api_key: e.target.value })}
                    data-testid="create-brand-api-key"
                  />
                  <p className="text-xs text-muted-foreground">
                    Unique API key for this brand to access Data Bridge
                  </p>
                </div>
              </div>
            </div>

            {/* Admin User Credentials */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Brand Admin User (Optional)
              </Label>
              <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-3">
                <p className="text-xs text-muted-foreground">
                  Create an admin user who can manage this brand. If left empty, you can add users later from Brand Users tab.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="admin-email" className="text-xs">Admin Email</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      placeholder="admin@brand.fundle.ai"
                      value={formData.admin_email || ''}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      data-testid="create-brand-admin-email"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="admin-password" className="text-xs">Password</Label>
                    <Input
                      id="admin-password"
                      type="password"
                      placeholder="Min 8 characters"
                      value={formData.admin_password || ''}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      data-testid="create-brand-admin-password"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="admin-name" className="text-xs">Admin Name</Label>
                  <Input
                    id="admin-name"
                    placeholder="Brand Admin"
                    value={formData.admin_name || ''}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    data-testid="create-brand-admin-name"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="create-brand-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Brand
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Brand Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit Brand
            </DialogTitle>
            <DialogDescription>
              Update settings for {selectedBrand?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateBrand} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Brand Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-brand-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Subdomain</Label>
              <div className="flex items-center px-3 py-2 bg-muted rounded-md text-sm">
                <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
                {selectedBrand?.subdomain}AI.fundledata.com
              </div>
              <p className="text-xs text-muted-foreground">Subdomain cannot be changed after creation</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-logo">Logo URL</Label>
              <Input
                id="edit-logo"
                placeholder="https://example.com/logo.png"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                data-testid="edit-brand-logo"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-primary">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="edit-primary"
                    value={formData.theme_primary_color}
                    onChange={(e) => setFormData({ ...formData, theme_primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme_primary_color}
                    onChange={(e) => setFormData({ ...formData, theme_primary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-secondary">Secondary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="edit-secondary"
                    value={formData.theme_secondary_color}
                    onChange={(e) => setFormData({ ...formData, theme_secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={formData.theme_secondary_color}
                    onChange={(e) => setFormData({ ...formData, theme_secondary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data Bridge Connection
              </Label>
              <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">API URL (Global)</Label>
                  <div className="flex items-center px-3 py-2 bg-background rounded-md text-sm border border-border">
                    <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
                    <span className="text-muted-foreground truncate">{globalDataBridgeUrl || 'Not configured'}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-api-key">API Key</Label>
                  <Input
                    id="edit-api-key"
                    type="password"
                    placeholder="Enter new API key to update"
                    value={formData.data_bridge_api_key}
                    onChange={(e) => setFormData({ ...formData, data_bridge_api_key: e.target.value })}
                    data-testid="edit-brand-api-key"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to keep existing API key
                  </p>
                </div>
              </div>
            </div>

            {/* Point Value */}
            <div className="space-y-2">
              <Label htmlFor="edit-point-value">Point Redemption Value (₹)</Label>
              <Input
                id="edit-point-value"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="1.00"
                value={formData.point_value}
                onChange={(e) => setFormData({ ...formData, point_value: parseFloat(e.target.value) || 1.0 })}
                data-testid="edit-brand-point-value"
              />
              <p className="text-xs text-muted-foreground">
                Value of 1 loyalty point in rupees (used for liability calculations)
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="edit-active"
                checked={formData.is_active !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="edit-active" className="cursor-pointer">Brand is active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="edit-brand-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Brand
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>Are you sure you want to delete <strong>{selectedBrand?.name}</strong>?</p>
              <p className="text-destructive font-medium">
                This will permanently delete the brand and ALL users associated with it. This action cannot be undone.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteBrand}
              disabled={submitting}
              data-testid="confirm-delete-brand-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Brand & All Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
